import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { OFF } from '../../lib/off';
import { Link, useNavigate } from 'react-router';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { ApiError, api, type ChangeSetView, type Overview, type QueueReport, type Wallboard } from '../../api/client';
import { ChartLegend, ColumnChart } from '../../components/charts';
import { ChangeSetDetail, ChangeSetFeed } from '../../components/changesets';
import { useTenant } from '../../components/Shell';
import { Button, Card, Drawer, Empty, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { agentTotals } from '../../lib/agents';
import { useAuth } from '../../lib/auth';
import { plural } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import { displayZone, todayIn } from '../../lib/time';
import { useData } from '../../lib/useData';
import { CALL_SERIES, clock, pointNames } from './Reports';

const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** "Good afternoon" in the display zone. */
function greeting(): string {
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone: displayZone() }).format(new Date()));
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

const KIND_CAPTION: Record<string, string> = {
  timeRule: 'Time of day rule',
  autoAttendant: 'Auto attendant',
  callQueue: 'Call queue',
  ringGroup: 'Ring group',
  person: 'Person',
  callPark: 'Call park',
  none: 'Nowhere'
};

/**
 * The company admin's home: whether they're open, what's happening now, today's calls, what needs
 * their attention, where the main number goes, and recent changes. New admins get a short
 * getting-started list first.
 */
export function TenantHome() {
  const { tenant } = useTenant();
  const { state } = useAuth();
  const d = tenant.domain;
  const base = `/t/${encodeURIComponent(d)}`;
  const ov = useData(() => api.overview(d), [d]);
  const [tick, setTick] = useState(0);
  // Live queue figures, every 10 seconds while the page is open.
  const board = useData(() => api.wallboard(d).catch(() => null), [d, tick]);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 10_000);
    return () => window.clearInterval(t);
  }, []);
  const changes = useData(() => api.changeSets(d), [d]);
  const [open, setOpen] = useState<ChangeSetView | null>(null);

  const first = (state?.user?.name || '').split(/\s+/)[0] || '';
  const o = ov.data;
  const dateText = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric', timeZone: displayZone() }).format(new Date());
  const welcome = Boolean(o?.checklist?.show);

  return (
    <>
      <PageHeader
        eyebrow={`${tenant.name} · ${dateText}`}
        title={welcome ? `Welcome to Stitch${first ? `, ${first}` : ''}` : `${greeting()}${first ? `, ${first}` : ''}`}
        chips={o?.open ? <OpenPill open={o.open.open} text={o.open.text} /> : undefined}
      />
      <div className="content home">
        {tenant.customer?.status === 'onboarding' && tenant.writable && (
          <div className="setup-banner">
            <span className="leland-mark" aria-hidden="true">
              <Icon name="spark" size={16} />
            </span>
            <span className="cell-stack grow">
              <strong>Let’s get your phone system set up</strong>
              <span className="muted">Upload a spreadsheet of your people and Leland builds it out with you, one step at a time.</span>
            </span>
            <Link className="btn primary" to={`${base}/setup`}>
              Get set up
            </Link>
          </div>
        )}
        {!tenant.writable && (
          <Notice tone="warn" title="This company is view-only here" icon="lock">
            {OFF.readOnly}
          </Notice>
        )}
        {ov.error ? (
          <Card>
            <LoadError error={ov.error} retry={ov.reload} />
          </Card>
        ) : !o ? (
          <Card>
            <Loading label="Loading your phone system" />
          </Card>
        ) : welcome ? (
          <>
            <p className="home-lede">Your phone system is set up and taking calls. Here’s what to check first.</p>
            <div className="home-grid">
              <GetStarted o={o} base={base} domain={d} onChange={ov.reload} />
              <div className="home-side">
                <RightNow board={board.data ?? null} o={o} base={base} compact />
                <YourSystem o={o} base={base} />
              </div>
            </div>
            <MainNumber o={o} base={base} domain={d} wide />
          </>
        ) : (
          <>
            <RightNow board={board.data ?? null} o={o} base={base} />
            <div className="home-grid">
              <TodaysCalls domain={d} base={base} timeZone={tenant.timeZone ?? displayZone()} />
              <NeedsAttention o={o} base={base} />
            </div>
            <div className="home-grid">
              <MainNumber o={o} base={base} domain={d} />
              <YourSystem o={o} base={base} />
            </div>
            <Card title="Recent changes" action={<Link to={`${base}/history`}>Change history</Link>}>
              {changes.error ? (
                <LoadError error={changes.error} retry={changes.reload} />
              ) : !changes.data ? (
                <Loading />
              ) : changes.data.changeSets.length === 0 ? (
                <Empty title="No changes yet">When you change people or call routing, each change shows up here and can be undone for 30 days.</Empty>
              ) : (
                <ChangeSetFeed items={changes.data.changeSets.slice(0, 4)} onOpen={setOpen} />
              )}
            </Card>
          </>
        )}
        {o && o.missing.length > 0 && <p className="muted small-print">Some figures couldn’t be read from your phone system just now: {o.missing.join(', ')}.</p>}
      </div>
      <Drawer open={!!open} onClose={() => setOpen(null)} title={open?.title ?? ''} subtitle={plural(open?.steps.length ?? 0, 'step')}>
        {open && (
          <ChangeSetDetail
            cs={open}
            extra={
              <Link to={`${base}/history`} className="btn secondary small" style={{ alignSelf: 'flex-start' }}>
                Open change history
              </Link>
            }
          />
        )}
      </Drawer>
    </>
  );
}

function OpenPill({ open, text }: { open: boolean; text: string }) {
  return (
    <span className={`open-pill ${open ? 'open' : 'closed'}`}>
      <span className="open-dot" aria-hidden="true" />
      {text}
    </span>
  );
}

// --- right now ---------------------------------------------------------------------------------

function RightNow({ board, o, base, compact }: { board: Wallboard | null; o: Overview; base: string; compact?: boolean }) {
  const queues = board?.queues ?? [];
  const waiting = queues.reduce((n, q) => n + q.waiting, 0);
  const longest = [...queues].filter((q) => q.waiting).sort((a, b) => (b.longestWaitSeconds ?? 0) - (a.longestWaitSeconds ?? 0))[0];
  // Each agent once across queues, as on the wallboard.
  const agents = agentTotals(queues);
  const ready = agents.ready;
  const onCall = agents.busy;
  const readyBy = queues
    .map((q) => ({ name: q.name, n: q.agents.filter((a) => a.state === 'available').length }))
    .filter((x) => x.n)
    .map((x) => `${x.name} ${x.n}`)
    .join(' · ');
  const phonesOffline = o.counts.phones !== null && o.counts.phonesOnline !== null ? o.counts.phones - o.counts.phonesOnline : 0;
  const tiles: { label: string; value: ReactNode; foot?: ReactNode }[] = [];
  if (queues.length) {
    tiles.push({
      label: 'Callers waiting',
      value: waiting,
      foot: longest ? `${longest.name} · waiting ${clock(longest.longestWaitSeconds ?? null)}` : 'Nobody waiting'
    });
    tiles.push({ label: 'Agents on a call', value: onCall, foot: `of ${plural(agents.total, 'queue agent')}` });
    tiles.push({
      label: 'Queue agents ready',
      value: (
        <>
          {ready}
          {/* Of those signed in, as on the wallboard (3136); everyone in a queue is in the foot. */}
          <small> of {agents.loggedIn} signed in</small>
        </>
      ),
      foot: readyBy ? `${readyBy} · ${plural(agents.total, 'queue agent')} in all` : `Nobody is ready · ${plural(agents.total, 'queue agent')} in all`
    });
  } else {
    tiles.push({ label: 'People', value: o.counts.people, foot: o.counts.mailboxes ? plural(o.counts.mailboxes, 'voicemail box', 'voicemail boxes') : 'With an extension' });
  }
  if (o.counts.phones) {
    tiles.push({
      label: 'Desk phones online',
      value: (
        <>
          {o.counts.phonesOnline ?? 0}
          <small> of {o.counts.phones}</small>
        </>
      ),
      foot: phonesOffline ? <span className="warn-text">{phonesOffline} offline</span> : 'All online'
    });
  }
  return (
    <Card
      title={
        <>
          Right now <span className="live-tag">Live</span>
        </>
      }
      action={queues.length ? <Link to={`${base}/wallboard`}>Open wallboard</Link> : undefined}
    >
      <div className={`now-tiles${compact ? ' compact' : ''}`}>
        {tiles.map((t) => (
          <div key={t.label} className="now-tile">
            <span className="now-label">{t.label}</span>
            <strong className="now-value">{t.value}</strong>
            {!compact && t.foot && <span className="now-foot">{t.foot}</span>}
          </div>
        ))}
      </div>
    </Card>
  );
}

// --- today's calls -------------------------------------------------------------------------------

function TodaysCalls({ domain, base, timeZone }: { domain: string; base: string; timeZone: string }) {
  const [period, setPeriod] = useState<'today' | 'week'>('today');
  const today = todayIn(timeZone);
  const from = new Date(today);
  if (period === 'week') from.setDate(from.getDate() - 6);
  const range = { from: isoDay(from), to: isoDay(today) };
  const report = useData(
    () =>
      api.queueReport(domain, range).catch((err) => {
        if (err instanceof ApiError && err.code === 'NOT_AVAILABLE') return null;
        throw err;
      }),
    [domain, range.from, range.to]
  );
  const r: QueueReport | null | undefined = report.data;
  const shown = useMemo(() => {
    if (!r) return [];
    if (r.by !== 'hour') return r.series;
    // From an hour before the first call (or 7 AM) to two hours from now.
    const firstCall = r.series.findIndex((p) => p.offered > 0);
    const start = Math.max(0, Math.min(firstCall < 0 ? 7 : firstCall - 1, 7));
    const now = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone }).format(new Date()));
    return r.series.slice(start, Math.min(24, Math.max(now + 3, start + 8)));
  }, [r, timeZone]);
  const t = r?.totals;
  const notAnswered = t ? t.missed + t.abandoned : 0;
  return (
    <Card
      title={period === 'today' ? 'Today’s calls' : 'This week’s calls'}
      action={
        <div className="segmented small" role="group" aria-label="Period">
          {(['today', 'week'] as const).map((p) => (
            <button key={p} type="button" aria-pressed={period === p} className={period === p ? 'on' : undefined} onClick={() => setPeriod(p)}>
              {p === 'today' ? 'Today' : 'This week'}
            </button>
          ))}
        </div>
      }
    >
      <div className="card-body">
        {report.error ? (
          <LoadError error={report.error} retry={report.reload} />
        ) : report.data === undefined ? (
          <Loading label="Loading calls" />
        ) : !r || !r.queues.length ? (
          <Empty title="Calls show here once you have a call queue">Calls into your call queues, answered and missed, hour by hour.</Empty>
        ) : (
          <>
            <div className="calls-totals">
              <div>
                <strong>{t!.offered.toLocaleString()}</strong>
                <span>calls in</span>
              </div>
              <div>
                <strong>{t!.answerRate === null ? '–' : `${Math.round(t!.answerRate * 100)}%`}</strong>
                <span>answered</span>
              </div>
              <div>
                <strong className={notAnswered ? 'crit-text' : undefined}>{notAnswered.toLocaleString()}</strong>
                <span>not answered</span>
              </div>
              <div>
                <strong>{clock(t!.avgWaitSeconds)}</strong>
                <span>average wait</span>
              </div>
            </div>
            <ColumnChart
              height={180}
              label={`${period === 'today' ? 'Calls by hour today' : 'Calls by day this week'}, answered and not answered`}
              series={CALL_SERIES}
              points={shown.map((pt) => ({
                key: pt.key,
                ...pointNames(r, pt.key),
                values: [pt.answered, pt.missed + pt.abandoned],
                detail: pt.offered ? `${pt.missed} missed · ${pt.abandoned} hung up · average wait ${clock(pt.avgWaitSeconds)}` : 'No calls'
              }))}
            />
            <div className="calls-foot">
              <ChartLegend series={CALL_SERIES} />
              <Link to={`${base}/reports?range=${period === 'today' ? 'today' : '7d'}`}>Open reports</Link>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// --- needs attention ------------------------------------------------------------------------------

const ATTENTION_ICON = (id: string): IconName => (id.startsWith('number') ? 'hash' : id.startsWith('phones') ? 'device' : id.startsWith('holiday') ? 'clock' : 'alert');

function NeedsAttention({ o, base }: { o: Overview; base: string }) {
  return (
    <Card
      title={
        <>
          Needs your attention {o.attention.length > 0 && <span className="count alert">{o.attention.length}</span>}
        </>
      }
    >
      {o.attention.length === 0 ? (
        <div className="card-body all-good">
          <span className="all-good-icon" aria-hidden="true">
            <Icon name="check" size={18} strokeWidth={2.4} />
          </span>
          <span>
            <strong>All good</strong>
            <span className="muted"> Nothing needs your attention right now.</span>
          </span>
        </div>
      ) : (
        <ul className="attention-list">
          {o.attention.map((a) => (
            <li key={a.id}>
              <span className={`attention-icon ${a.tone}`} aria-hidden="true">
                <Icon name={ATTENTION_ICON(a.id)} size={17} />
              </span>
              <span className="attention-text">
                <strong>{a.title}</strong>
                <span className="muted">{a.detail}</span>
                <Link to={`${base}/${a.action.to}`}>{a.action.label}</Link>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// --- the main number ------------------------------------------------------------------------------

function MainNumber({ o, base, domain, wide }: { o: Overview; base: string; domain: string; wide?: boolean }) {
  const navigate = useNavigate();
  const f = o.flow;
  const menu = f?.steps.find((s) => s.kind === 'autoAttendant');
  const flowLink = `${base}/auto-attendants?flow=${menu ? encodeURIComponent(menu.extension) : '1'}`;
  const openFlow = () => {
    if (o.checklist) void api.checklist(domain, { done: 'flow' }).catch(() => undefined);
    navigate(flowLink);
  };
  if (!f) {
    return (
      <Card title="Where your main number goes">
        <Empty title="No phone numbers yet">Your phone numbers, and where calls to each one go, show here once they’re on your phone system.</Empty>
      </Card>
    );
  }
  const label = f.state === 'business' ? 'Right now · business hours' : f.state === 'afterHours' ? 'Right now · after hours' : 'Right now';
  return (
    <section className={`card main-number${wide ? ' wide' : ''}`} aria-labelledby="main-number-title">
      <div className="card-head">
        <span className="cell-stack">
          <h2 id="main-number-title">Where your main number goes</h2>
          <span className="muted">
            {formatPhoneNumber(f.number)} · {f.label}
          </span>
        </span>
        {menu && (
          <button type="button" className="link-btn" onClick={openFlow}>
            Open the call flow
          </button>
        )}
      </div>
      <div className="card-body flow-body">
        <span className={`flow-state ${f.state}`}>{label}</span>
        <ol className="flow-steps">
          {f.steps.map((s, i) => (
            <li key={`${s.extension}-${i}`} className={`flow-step${s.kind === 'none' ? ' nowhere' : ''}`}>
              <span className="flow-caption">{s.via ?? KIND_CAPTION[s.kind] ?? 'Then'}</span>
              <strong>{s.name}</strong>
              <span className={s.kind === 'timeRule' ? (s.detail === 'Open now' ? 'ok-text' : 'warn-text') : 'muted'}>{s.detail}</span>
            </li>
          ))}
        </ol>
        {f.other && <p className="flow-other">{f.other}</p>}
        {f.nextHoliday && (
          <p className="flow-holiday muted">
            <Icon name="clock" size={15} /> Next holiday: {f.nextHoliday}
          </p>
        )}
      </div>
    </section>
  );
}

// --- your phone system ----------------------------------------------------------------------------

function YourSystem({ o, base }: { o: Overview; base: string }) {
  const c = o.counts;
  const rows: { icon: IconName; title: string; detail: string; to: string }[] = [
    { icon: 'users', title: 'People', detail: `${plural(c.people, 'person', 'people')}${c.mailboxes ? ` · ${plural(c.mailboxes, 'voicemail box', 'voicemail boxes')}` : ''}`, to: 'people' }
  ];
  if (c.phones !== null) rows.push({ icon: 'device', title: 'Desk phones', detail: `${plural(c.phones, 'phone')} · ${c.phonesOnline ?? 0} online`, to: 'phones' });
  if (c.numbers !== null) rows.push({ icon: 'hash', title: 'Numbers', detail: `${plural(c.numbers, 'number')}${c.numbersNowhere ? ` · ${c.numbersNowhere} ${c.numbersNowhere === 1 ? 'goes' : 'go'} nowhere` : ''}`, to: 'numbers' });
  if (c.queues.length) rows.push({ icon: 'headset', title: 'Call queues', detail: `${c.queues.slice(0, 3).join(', ')} · ${plural(c.agents, 'agent')}`, to: 'queues' });
  rows.push({
    icon: 'key',
    title: 'Portal access',
    detail: `${plural(c.admins, 'admin')}${c.endUsers ? ` · ${c.endUsers} see their own phone` : ''}${c.invited ? ` · ${c.invited} invited` : ''}`,
    to: 'team'
  });
  return (
    <Card title="Your phone system">
      <ul className="system-list">
        {rows.map((r) => (
          <li key={r.title}>
            <Link to={`${base}/${r.to}`}>
              <Icon name={r.icon} size={18} />
              <span className="cell-stack">
                <strong>{r.title}</strong>
                <span className="muted">{r.detail}</span>
              </span>
              <Icon name="chevRight" size={16} />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// --- getting started ------------------------------------------------------------------------------

function GetStarted({ o, base, domain, onChange }: { o: Overview; base: string; domain: string; onChange: () => void }) {
  const navigate = useNavigate();
  const list = o.checklist!;
  const next = list.steps.find((s) => !s.done);
  const go = (id: string, to: string) => {
    // Looking is doing, for these two.
    if (id === 'flow' || id === 'phones') void api.checklist(domain, { done: id }).catch(() => undefined);
    navigate(`${base}/${to}`);
  };
  return (
    <section className="card get-started" aria-labelledby="get-started-title">
      <div className="card-head">
        <h2 id="get-started-title">Get started</h2>
        <span className="muted">
          <strong>
            {list.done} of {list.steps.length}
          </strong>{' '}
          done
        </span>
      </div>
      <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={list.steps.length} aria-valuenow={list.done} aria-label="Getting started">
        <span style={{ width: `${(list.done / list.steps.length) * 100}%` }} />
      </div>
      <ol className="start-steps">
        {list.steps.map((s, i) => (
          <li key={s.id} className={s.done ? 'done' : undefined}>
            <span className="start-mark" aria-hidden="true">
              {s.done ? <Icon name="check" size={15} strokeWidth={2.6} /> : i + 1}
            </span>
            <span className="cell-stack">
              <strong>{s.title}</strong>
              <span className="muted">{s.detail}</span>
            </span>
            {!s.done && s.action && (
              <Button variant={s.id === next?.id ? 'primary' : 'secondary'} size="small" onClick={() => go(s.id, s.action!.to)}>
                {s.action.label}
              </Button>
            )}
            {s.done && <span className="sr-only">Done</span>}
          </li>
        ))}
      </ol>
      <div className="card-foot get-started-foot">
        <span className="muted">This list goes away once everything is done.</span>
        <button
          type="button"
          className="link-btn"
          onClick={() => {
            void api
              .checklist(domain, { hide: true })
              .then(onChange)
              .catch(() => undefined);
          }}
        >
          Hide for now
        </button>
      </div>
    </section>
  );
}
